import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';
import { useSSEStream } from '../../../hooks/useSSEStream';
import api from '../../../lib/api';
import type { OrchestratorStreamEvent, PendingApprovalAction } from '../types';
import { CHAT_KEYS } from './useConversations';

/**
 * Orchestrator tools that mutate a lead. After any of these, the lead detail
 * cache (`['lead', id, organizationId]`, src/hooks/useLeads.ts) is stale — the
 * write happened server-side, so nothing on the client knows to refetch.
 *
 * Read-only tools are deliberately absent: invalidating on every search would
 * refetch the open lead on each turn and defeat the cache.
 */
const LEAD_MUTATING_TOOLS: ReadonlySet<string> = new Set([
  'create_lead',
  'create_leads_from_research',
  'update_lead_fields',
  'add_tags',
  'add_note',
  'assign_lead',
  'move_lead_stage',
  'record_lead_consent',
  'request_lead_consent',
  'enrich_profile',
]);

/**
 * The CRM tools return the lead under two different shapes: the safe-summary
 * tools put it in `id`, while `move_lead_stage` and `add_note` return `leadId`
 * alongside their own result fields. `leadId` wins because a tool that sends
 * both means that one.
 */
function leadIdFromToolOutput(output: unknown): string | undefined {
  if (!output || typeof output !== 'object') return undefined;
  const candidate = output as { id?: unknown; leadId?: unknown };
  if (typeof candidate.leadId === 'string') return candidate.leadId;
  if (typeof candidate.id === 'string') return candidate.id;
  return undefined;
}

export interface UseChatStreamOptions {
  conversationId: string | null;
  organizationId: string | undefined;
  getAccessToken?: () => Promise<string | null>;
  onEvent?: (event: OrchestratorStreamEvent) => void;
  onMessageStop?: (event: { message: string; iterations: number }) => void;
  onProspectSearchInvoked?: (sessionId: string) => void;
  onRunCancelled?: (event: { executedActions: Array<{ name: string; label: string }> }) => void;
}

export interface ChatStatus {
  message: string;
  stage: 'started' | 'thinking' | 'tool' | 'finalizing';
  toolName?: string;
}

/**
 * Distinguishes an error the AI orchestrator reported (`'orchestrator'`) from a
 * client/stream-side failure (`'stream'`). The orchestrator sanitizes its real
 * cause to a generic string server-side, so the UI shows a friendly,
 * admin-oriented message for `'orchestrator'` instead of the raw text.
 */
export type ChatErrorKind = 'orchestrator' | 'stream' | null;

export interface UseChatStreamReturn {
  isStreaming: boolean;
  isConnected: boolean;
  error: string | null;
  errorKind: ChatErrorKind;
  events: OrchestratorStreamEvent[];
  currentText: string;
  finalMessage: string | null;
  activeRunId: string | null;
  status: ChatStatus | null;
  pendingAction: PendingApprovalAction | null;
  isResolvingPendingAction: boolean;
  approvePendingAction: (itemIds?: readonly string[]) => Promise<void>;
  rejectPendingAction: () => Promise<void>;
  sendMessage: (
    content: string,
    conversationIdOverride?: string,
    prospectSessionId?: string,
  ) => Promise<void>;
  /** Stop the in-progress run. The server emits run-cancelled, then completes. */
  cancel: () => Promise<void>;
  /** Edit-and-resend: replaces the last user turn and starts a fresh run. */
  editAndResend: (
    content: string,
    conversationIdOverride?: string,
    prospectSessionId?: string,
  ) => Promise<void>;
  reset: () => void;
}

export function useChatStream({
  conversationId,
  organizationId,
  getAccessToken,
  onEvent,
  onMessageStop,
  onProspectSearchInvoked,
  onRunCancelled,
}: UseChatStreamOptions): UseChatStreamReturn {
  const queryClient = useQueryClient();
  const [events, setEvents] = useState<OrchestratorStreamEvent[]>([]);
  const [currentText, setCurrentText] = useState('');
  const [finalMessage, setFinalMessage] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorKind, setErrorKind] = useState<ChatErrorKind>(null);
  const [status, setStatus] = useState<ChatStatus | null>(null);
  const [pendingAction, setPendingAction] = useState<PendingApprovalAction | null>(null);
  const [isResolvingPendingAction, setIsResolvingPendingAction] = useState(false);

  const onEventRef = useRef(onEvent);
  const onMessageStopRef = useRef(onMessageStop);
  const onProspectSearchInvokedRef = useRef(onProspectSearchInvoked);
  const onRunCancelledRef = useRef(onRunCancelled);
  const runConversationIdRef = useRef<string | null>(null);
  onEventRef.current = onEvent;
  onMessageStopRef.current = onMessageStop;
  onProspectSearchInvokedRef.current = onProspectSearchInvoked;
  onRunCancelledRef.current = onRunCancelled;

  const sendMutation = useMutation({
    mutationFn: async ({
      content,
      conversationId: targetConversationId,
      prospectSessionId,
    }: {
      content: string;
      conversationId: string;
      prospectSessionId?: string;
    }) => {
      if (!organizationId) {
        throw new Error('Missing organization context');
      }
      const res = await api.post<{ runId: string }>(
        `/chat/conversations/${targetConversationId}/messages`,
        { content },
        { params: { organizationId, ...(prospectSessionId ? { prospectSessionId } : {}) } },
      );
      return res.data.runId;
    },
  });

  /**
   * The chat screen appends only the final assistant text to the messages
   * cache, which is held forever (staleTime: Infinity). The persisted row also
   * carries the tool calls behind the result cards, so refetch once the server
   * has written it. Both message-stop and run-cancelled are emitted after that
   * write; an error can precede it, so it is deliberately not a trigger.
   */
  const refreshConversationMessages = useCallback(() => {
    const runConversationId = runConversationIdRef.current;
    if (!organizationId || !runConversationId) return;
    queryClient.invalidateQueries({
      queryKey: CHAT_KEYS.messages(organizationId, runConversationId),
    });
  }, [organizationId, queryClient]);

  const handleMessage = useCallback(
    (data: string) => {
      let parsed: { type: string } & Record<string, unknown>;
      try {
        parsed = JSON.parse(data);
      } catch {
        return;
      }

      if (parsed.type === '_done') {
        setIsStreaming(false);
        setActiveRunId(null);
        return;
      }

      const event = parsed as unknown as OrchestratorStreamEvent;
      setEvents((prev) => [...prev, event]);
      onEventRef.current?.(event);

      switch (event.type) {
        case 'status':
          setStatus({
            message: event.message,
            stage: event.stage,
            toolName: event.toolName,
          });
          break;
        case 'text':
          setCurrentText(event.content);
          break;
        case 'message-stop':
          setFinalMessage(event.message);
          setStatus(null);
          setPendingAction(null);
          // Defensively close the SSE connection on message-stop instead of
          // waiting for the server's _done sentinel. If _done is dropped (network
          // hiccup, process restart), the EventSource would otherwise stay open
          // and isStreaming would never flip back to false.
          setIsStreaming(false);
          setActiveRunId(null);
          onMessageStopRef.current?.({
            message: event.message,
            iterations: event.iterations,
          });
          refreshConversationMessages();
          break;
        case 'error':
          setError(event.error);
          setErrorKind('orchestrator');
          setStatus(null);
          setPendingAction(null);
          // Mirror message-stop: don't wait for the server's _done sentinel
          // to re-enable the composer. The server has already confirmed the
          // run failed; if _done is dropped, isStreaming would otherwise
          // stay true forever and lock the input.
          setIsStreaming(false);
          setActiveRunId(null);
          break;
        case 'run-cancelled':
          setStatus(null);
          setPendingAction(null);
          setIsResolvingPendingAction(false);
          // Mirror message-stop: re-enable the composer immediately; the server
          // completes the stream right after this event.
          setIsStreaming(false);
          setActiveRunId(null);
          onRunCancelledRef.current?.({ executedActions: event.executedActions });
          refreshConversationMessages();
          break;
        case 'tool-pending-approval':
          setPendingAction({
            pendingActionId: event.pendingActionId,
            toolName: event.toolName,
            input: event.input,
            expiresAt: event.expiresAt,
          });
          break;
        case 'tool-call-complete':
          // The orchestrator loop runs one tool call at a time (see
          // runToolCall in orchestrator.service.ts), so a tool-call-complete
          // always means any pending approval for the current call has
          // already been resolved — safe to clear unconditionally here.
          setPendingAction(null);
          setIsResolvingPendingAction(false);
          if (LEAD_MUTATING_TOOLS.has(event.name)) {
            // The list is always refreshed; the detail entry only when the tool
            // told us which lead it touched. A tool that reports no lead id (a
            // failed create, say) still leaves the list worth refetching.
            const leadId = leadIdFromToolOutput(event.output);
            if (leadId) {
              queryClient.invalidateQueries({ queryKey: ['lead', leadId] });
            }
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            if (event.name === 'move_lead_stage') {
              queryClient.invalidateQueries({ queryKey: ['board'] });
            }
          }
          if (
            event.name === 'invoke_prospect_search' &&
            event.output &&
            typeof event.output === 'object' &&
            (('sessionRef' in event.output &&
              typeof (event.output as { sessionRef: unknown }).sessionRef === 'string') ||
              ('sessionId' in event.output &&
                typeof (event.output as { sessionId: unknown }).sessionId === 'string'))
          ) {
            const output = event.output as { sessionRef?: string; sessionId?: string };
            const safeSessionRef = output.sessionRef ?? output.sessionId;
            if (safeSessionRef) {
              onProspectSearchInvokedRef.current?.(safeSessionRef);
            }
          }
          break;
      }
    },
    [queryClient, refreshConversationMessages],
  );

  const resolvePendingAction = useCallback(
    async (decision: 'approve' | 'reject', itemIds?: readonly string[]) => {
      if (!pendingAction || !organizationId) return;
      setIsResolvingPendingAction(true);
      try {
        await api.post(
          `/chat/pending-actions/${pendingAction.pendingActionId}/${decision}`,
          itemIds ? { itemIds } : undefined,
          { params: { organizationId } },
        );
      } catch (err) {
        const msg = err instanceof Error ? err.message : `Failed to ${decision} the action`;
        setError(msg);
        setErrorKind('stream');
        setIsResolvingPendingAction(false);
      }
      // On success, deliberately don't clear pendingAction/isResolvingPendingAction
      // here — the resumed orchestrator run will emit tool-call-complete next,
      // which is the authoritative signal (see the switch case above).
    },
    [pendingAction, organizationId],
  );

  const approvePendingAction = useCallback(
    (itemIds?: readonly string[]) => resolvePendingAction('approve', itemIds),
    [resolvePendingAction],
  );
  const rejectPendingAction = useCallback(
    () => resolvePendingAction('reject'),
    [resolvePendingAction],
  );

  const { isConnected } = useSSEStream({
    path: activeRunId
      ? `chat/conversations/${conversationId}/runs/${activeRunId}/stream`
      : 'chat/conversations/no-run/stream',
    organizationId,
    enabled: !!activeRunId && !!conversationId,
    getAccessToken,
    onMessage: handleMessage,
  });

  const sendMessage = useCallback(
    // conversationIdOverride lets the caller pass a freshly-resolved id
    // (e.g. from a just-created conversation) without waiting for React
    // to commit the parent's setActiveConversationId. The hook's prop
    // closes over the previous value until re-render.
    async (content: string, conversationIdOverride?: string, prospectSessionId?: string) => {
      const targetConversationId = conversationIdOverride ?? conversationId;
      if (!targetConversationId) {
        setError('No conversation selected');
        setErrorKind('stream');
        return;
      }
      setError(null);
      setErrorKind(null);
      setEvents([]);
      setCurrentText('');
      setFinalMessage(null);
      setStatus(null);
      setIsStreaming(true);
      runConversationIdRef.current = targetConversationId;

      try {
        const runId = await sendMutation.mutateAsync({
          content,
          conversationId: targetConversationId,
          prospectSessionId,
        });
        setActiveRunId(runId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to send message';
        setError(msg);
        setErrorKind('stream');
        setIsStreaming(false);
      }
    },
    [sendMutation, conversationId],
  );

  const cancel = useCallback(async () => {
    if (!activeRunId || !conversationId || !organizationId) return;
    try {
      await api.post(
        `/chat/conversations/${conversationId}/runs/${activeRunId}/cancel`,
        undefined,
        { params: { organizationId } },
      );
      // Don't flip isStreaming here — wait for the authoritative run-cancelled
      // (or _done) event so the "what already ran" report renders in order.
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to cancel';
      setError(msg);
      setErrorKind('stream');
    }
  }, [activeRunId, conversationId, organizationId]);

  const editAndResend = useCallback(
    async (content: string, conversationIdOverride?: string, prospectSessionId?: string) => {
      const targetConversationId = conversationIdOverride ?? conversationId;
      if (!targetConversationId || !organizationId) {
        setError('No conversation selected');
        setErrorKind('stream');
        return;
      }
      setError(null);
      setErrorKind(null);
      setEvents([]);
      setCurrentText('');
      setFinalMessage(null);
      setStatus(null);
      setIsStreaming(true);
      runConversationIdRef.current = targetConversationId;

      try {
        const res = await api.post<{ runId: string }>(
          `/chat/conversations/${targetConversationId}/messages/edit-resend`,
          { content },
          { params: { organizationId, ...(prospectSessionId ? { prospectSessionId } : {}) } },
        );
        setActiveRunId(res.data.runId);
      } catch (err) {
        const msg = err instanceof Error ? err.message : 'Failed to resend message';
        setError(msg);
        setErrorKind('stream');
        setIsStreaming(false);
      }
    },
    [conversationId, organizationId],
  );

  const reset = useCallback(() => {
    setEvents([]);
    setCurrentText('');
    setFinalMessage(null);
    setActiveRunId(null);
    setError(null);
    setErrorKind(null);
    setIsStreaming(false);
    setStatus(null);
    setPendingAction(null);
    setIsResolvingPendingAction(false);
  }, []);

  return {
    isStreaming,
    isConnected,
    error,
    errorKind,
    events,
    currentText,
    finalMessage,
    activeRunId,
    status,
    pendingAction,
    isResolvingPendingAction,
    approvePendingAction,
    rejectPendingAction,
    sendMessage,
    cancel,
    editAndResend,
    reset,
  };
}
